import { useEffect, useState } from "react";
import FilePreviewModal from "../filePreviewModal";
import { API_BASE } from "../../config/apiConfig";
import { useToast } from "../../hooks/useToast";
import LoadingOverlay from "../../components/loadingOverlay";

export default function AttendanceList({ eventId }) {
  const [players, setPlayers] = useState([]);
  const [eventStart, setEventStart] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = sessionStorage.getItem("access_token");
  const { showToast } = useToast();

  useEffect(() => {
    if (!eventId) return;
    loadData();
  }, [eventId]);

  async function loadData() {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/attendance/event/${eventId}/full-list`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const data = await res.json();
      if (!data.success) {
        showToast("Failed to load attendance list", "error");
        return;
      }

      setPlayers(data.players);
      setEventStart(new Date(data.eventStart));
    } catch (err) {
      console.error(err);
      showToast("Error loading attendance list", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <ul className="divide-y">
      <LoadingOverlay show={loading} text="Loading..." />
      {players.map((player, idx) => {
        const rec = player.attendance;
        const leave = player.leave;

        let statusNode = <span className="text-red-600">Absent</span>;

        // -----------------------------------------------------
        // 1. LEAVE FOUND
        // -----------------------------------------------------
        if (leave) {
          if (leave.type === "Medical Leave") {
            statusNode = (
              <div className="flex flex-col items-end text-blue-600">
                <div className="flex items-center gap-2">
                  <span>MC</span>
                  {leave.documentUrl && (
                    <button
                      onClick={() => setPreviewUrl(leave.documentUrl)}
                      className="underline text-blue-600 text-sm"
                    >
                      View Doc
                    </button>
                  )}
                </div>
                {leave.remark && (
                  <span className="text-sm text-gray-400 italic">
                    Remark: {leave.remark}
                  </span>
                )}
              </div>
            );
          } else if (leave.type === "Emergency Leave") {
            statusNode = (
              <div className="flex flex-col items-end">
                <span className="text-pink-600 font-semibold">
                  Emergency Leave
                </span>
                {leave.emergencyReason && (
                  <span className="text-sm text-gray-500">
                    Reason: {leave.emergencyReason}
                  </span>
                )}
                {leave.remark && (
                  <span className="text-sm text-gray-400 italic">
                    Remark: {leave.remark}
                  </span>
                )}
              </div>
            );
          } else if (
            leave.type === "Tournament Leave" ||
            leave.type === "Event Leave"
          ) {
            statusNode = (
              <div className="flex flex-col items-end">
                <span
                  className={
                    leave.type === "Tournament Leave"
                      ? "text-purple-600 font-semibold"
                      : "text-green-600 font-semibold"
                  }
                >
                  {leave.type.replace(" Leave", "")}
                </span>

                {leave.activityName && (
                  <span className="text-sm text-gray-500">
                    {leave.activityName}
                  </span>
                )}

                {leave.remark && (
                  <span className="text-sm text-gray-400 italic">
                    Remark: {leave.remark}
                  </span>
                )}
              </div>
            );
          }
        }

        // -----------------------------------------------------
        // 2. HAS ATTENDANCE
        // -----------------------------------------------------
        else if (rec?.createdAt) {
          const ts = new Date(rec.createdAt);
          const eventStartTime = eventStart;

          let statusColor = "text-green-600";
          let statusText = "Present";

          if (eventStartTime && ts >= new Date(eventStartTime.getTime() + 60 * 1000)) {
            const lateMinutes = Math.floor((ts - eventStartTime) / (60 * 1000));
            statusColor = "text-orange-500";
            statusText = `Late (${lateMinutes} min${lateMinutes > 1 ? "s" : ""})`;
          }

          const time = ts.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          });

          statusNode = (
            <div className={`${statusColor} flex flex-col text-right`}>
              <span>{statusText}</span>
              <span className="text-sm text-gray-500">Checked in: {time}</span>
            </div>
          );
        }

        return (
          <li
            key={player.user_id}
            className="py-1 flex justify-between items-center"
          >
            <span>
              {idx + 1}. {player.fullName}
            </span>
            {statusNode}
          </li>
        );
      })}

      <FilePreviewModal
        url={previewUrl}
        onClose={() => setPreviewUrl(null)}
      />
    </ul>
  );
}
